import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FieldHint } from '@/components/field-hint'
import { ChipInput } from '@/components/chip-input'
import { LocationChipInput } from '@/components/location-chip-input'
import { INDUSTRIES } from '@dealcloser/shared'
import type { SearchFilters } from './use-search'

export type InitialSearchFilters = {
  readonly title?: readonly string[]
  readonly industries?: readonly string[]
  readonly location?: readonly string[]
  readonly keywords?: string
}

type SearchFiltersProps = {
  readonly onSearch: (filters: SearchFilters) => void
  readonly isLoading?: boolean
  readonly initialFilters?: InitialSearchFilters
}

export function SearchFiltersPanel({ onSearch, isLoading, initialFilters }: SearchFiltersProps) {
  const [title, setTitle] = useState<readonly string[]>([])
  const [company, setCompany] = useState('')
  const [industries, setIndustries] = useState<readonly string[]>([])
  const [location, setLocation] = useState<readonly string[]>([])
  const [keywords, setKeywords] = useState<readonly string[]>([])

  // Populate from ICP settings once on first arrival — never overwrite user edits
  const initialAppliedRef = useRef(false)
  useEffect(() => {
    if (!initialFilters || initialAppliedRef.current) return
    initialAppliedRef.current = true
    if (initialFilters.title) setTitle(initialFilters.title)
    if (initialFilters.industries && initialFilters.industries.length > 0) setIndustries(initialFilters.industries)
    if (initialFilters.location) setLocation(initialFilters.location)
    if (initialFilters.keywords) {
      // Legacy ICP keywords arrive as a single comma-separated string,
      // split into chips so it matches the new chip input shape.
      const parts = initialFilters.keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean)
      if (parts.length > 0) setKeywords(parts)
    }
  }, [initialFilters])

  function toggleIndustry(ind: string) {
    setIndustries((prev) =>
      prev.includes(ind) ? prev.filter((i) => i !== ind) : [...prev, ind],
    )
  }

  function handleSearch() {
    onSearch({
      title: title.join(', '),
      company,
      industry: industries.join(', '),
      location: location.join(', '),
      keywords: keywords.join(', '),
    })
  }

  function handleClear() {
    setTitle([])
    setCompany('')
    setIndustries([])
    setLocation([])
    setKeywords([])
    onSearch({})
  }

  const hasAnyFilter =
    title.length > 0 || company || industries.length > 0 || location.length > 0 || keywords.length > 0

  // Enter NEVER kicks off the search. The chip inputs use Enter to lock
  // a chip, and a user filling multiple chips (e.g., "ceo" → enter →
  // "cto" → enter) used to accidentally fire a search on the first
  // chip. Only the explicit "Search Leads" button starts a search.
  return (
    <div className="flex flex-col gap-4">
      <ChipInput
        label="Job Title"
        placeholder="e.g. CEO"
        chips={title}
        onChange={setTitle}
        disabled={isLoading}
        hint="Roles you want to reach, like CEO or VP Sales. Add several and press Enter after each to match any of them."
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <Label htmlFor="filter-company">Company</Label>
          <FieldHint label="What is the Company filter?">
            Narrow results to people at one company by name.
          </FieldHint>
        </div>
        <Input
          id="filter-company"
          placeholder="e.g. Acme Corp"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-1">
          <Label>Industry</Label>
          <FieldHint label="What is the Industry filter?">
            Click one or more industries to include. Click a pill again to remove it.
          </FieldHint>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {INDUSTRIES.map((ind) => (
            <button
              key={ind}
              type="button"
              onClick={() => toggleIndustry(ind)}
              className={[
                'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                industries.includes(ind)
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-transparent text-foreground hover:bg-muted',
              ].join(' ')}
            >
              {ind}
            </button>
          ))}
        </div>
      </div>

      <LocationChipInput
        label="Location"
        placeholder="Search states, cities, countries..."
        chips={location}
        onChange={setLocation}
        disabled={isLoading}
        hint="Filter by country, US state, or metro. Pick from the list or type your own, then press Enter."
      />

      <ChipInput
        label="Keywords"
        placeholder="e.g. SaaS"
        chips={keywords}
        onChange={setKeywords}
        disabled={isLoading}
        hint="Extra terms to match in a profile, like saas or fintech. Press Enter after each one."
      />

      <Button onClick={handleSearch} disabled={isLoading} className="mt-1 w-full">
        <Search className="mr-2 h-4 w-4" />
        {isLoading ? 'Searching...' : 'Search Leads'}
      </Button>

      {hasAnyFilter && (
        <button
          type="button"
          onClick={handleClear}
          className="flex items-center justify-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
        >
          <X className="h-3 w-3" />
          Clear Filters
        </button>
      )}
    </div>
  )
}
